import React from 'react';
import { injectIntl, WrappedComponentProps } from 'react-intl';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { translations } from '@/locale';
import { DiningSlotAdd } from '@/domains';

import { InstanceProps } from '@/components/Modal';
import ModalHeader from '@/components/Modal/ModalHeader';
import Typography from '@/components/Typography';
import { createOrganizationSpot } from '@/services/api/organizations';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';

const fields = translations.inputs;

const formSchema = z.object({
  startTime: z.string().min(1, 'Start time is required'),
  endTime: z.string().min(1, 'End time is required'),
  limit: z.number().min(1, 'Limit must be at least 1')
});

type FormValues = z.infer<typeof formSchema>;

export interface Props extends InstanceProps {
  orgId: string;
  type: 'add' | 'edit';
  onSaved: () => any;
}

const TimeSlotModal: React.FC<Props & WrappedComponentProps> = ({ close, orgId, type, onSaved, intl }) => {
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      startTime: '',
      endTime: '',
      limit: 1
    }
  });

  async function onSubmit(values: FormValues) {
    try {
      await createOrganizationSpot(orgId, values as DiningSlotAdd);
      onSaved();
      close();
    } catch (error) {
      console.error('Failed to save time slot:', error);
    }
  }

  return (
    <Card className="w-[450px]">
      <CardHeader>
        <ModalHeader close={close}>
          <Typography is="span" className="font-bold">
            {type === 'add' ? 'Add dining slot' : 'Edit dining slot'}
          </Typography>
        </ModalHeader>
      </CardHeader>

      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="flex space-x-4">
              <FormField
                control={form.control}
                name="startTime"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormLabel>{intl.formatMessage({ id: fields.startTime.label })}</FormLabel>
                    <FormControl>
                      <Input placeholder={intl.formatMessage({ id: fields.startTime.placeholder })} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="endTime"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormLabel>{intl.formatMessage({ id: fields.endTime.label })}</FormLabel>
                    <FormControl>
                      <Input placeholder={intl.formatMessage({ id: fields.endTime.placeholder })} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="limit"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{intl.formatMessage({ id: fields.spotLimit.label })}</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min={1}
                      placeholder={intl.formatMessage({ id: fields.spotLimit.placeholder })}
                      {...field}
                      onChange={(e) => field.onChange(parseInt(e.target.value))}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button type="submit" className="w-full" disabled={!form.formState.isDirty || form.formState.isSubmitting}>
              Save
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};

export default injectIntl(TimeSlotModal);
